import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import { MenuList } from '@atlaskit/navigation-system';
import { Box } from '@atlaskit/primitives/box';
import Stack from '@atlaskit/primitives/stack';
import GlobalShortcuts, { type GlobalShortcutsItem } from '@atlassian/global-shortcuts';
import { L1_MENU_GROUPS } from '@atlassian/navigation-common';
import {
	GroupDropIndicatorWithDragOver,
	isL1ItemData,
	useDragState,
} from '@atlassian/navigation-drag-and-drop';

import type { GlobalSideNavigationProps } from '../../common/types';
import NavItem from '../../common/ui/nav-item';
import { useInViewport } from '../../common/utils/use-in-viewport';
import { useNavAnalytics } from '../../common/utils/use-nav-analytics';
import { useItemsAndShortcuts } from '../../controllers/items-and-shortcuts-provider';
import { useOptions } from '../../controllers/options-provider';
import { usePeekMenuId } from '../../controllers/peek-menu-id-providers';
import { useGlobalShortcutsTransformation } from '../../controllers/transformation/use-global-shortcuts-transformation';
import { useUserMenuDataShapes } from '../../controllers/user-state/user-menu-data-shapes-provider';
import { PEEK_TEST_ID, SHORTCUTS_TEST_ID } from '../constants';
import MoreMenu from '../more-menu';
import { INTERNAL_GLOBAL_ITEMS } from '../more-menu/constants';
import { HideFromSidebarButton } from '../more-menu/hide-from-sidebar-button';
import { ShowInSidebarButton } from '../more-menu/show-in-sidebar-button';
import { messages } from '../side-nav/messages';

export function PostMainItems({
	postItemsElem,
	postMainElem,
	draggingL1Group = null,
	openShortcutsInSameTab,
}: PostMainItemsProps): React.JSX.Element {
	const { appLabel, disableMenuCustomization, l1DragAndDropEnabled } = useOptions();
	const { formatMessage } = useIntl();

	const { allItems, growthItemShortcuts, shortcuts } = useItemsAndShortcuts();
	const peekMenuId = usePeekMenuId();

	const { hiddenItemMenuIds, hiddenShortcutMenuIds, visibleShortcutMenuIds, hiddenMenuIds } =
		useUserMenuDataShapes();

	const { fireClickedSideNavItemEvent } = useNavAnalytics();

	const containerRef = useRef(null);
	const { isInViewport } = useInViewport({ ref: containerRef });

	const { shortcutsWithMenuId } = useGlobalShortcutsTransformation({
		shortcutsConfig: shortcuts,
		visibleShortcuts: visibleShortcutMenuIds,
		growthShortcutsConfig: growthItemShortcuts,
		l1DragAndDropEnabled,
	});

	const { groupDropIndicatorRef, dragSource } = useDragState({
		dragAndDropEnabled: l1DragAndDropEnabled,
		isItemData: isL1ItemData,
	});

	const handleClickMoreMenuItem = useCallback(() => {
		fireClickedSideNavItemEvent(INTERNAL_GLOBAL_ITEMS.more, 'postMain');
	}, [fireClickedSideNavItemEvent]);

	const handleClickCustomizeItem = useCallback(() => {
		fireClickedSideNavItemEvent(INTERNAL_GLOBAL_ITEMS.customize, 'postMain');
	}, [fireClickedSideNavItemEvent]);

	const peekItem = !!peekMenuId && hiddenItemMenuIds.includes(peekMenuId) && allItems[peekMenuId];
	const isDragSourceHidden = dragSource && hiddenMenuIds.includes(dragSource);

	const canMenuItemBeHidden = useMemo(() => {
		return !!(draggingL1Group && dragSource && allItems[dragSource]?.isHideable !== false);
	}, [allItems, dragSource, draggingL1Group]);

	const moreMenu = (
		<MoreMenu
			items={allItems}
			shortcutItems={shortcuts}
			itemIds={hiddenItemMenuIds}
			shortcutItemIds={hiddenShortcutMenuIds}
			appLabel={appLabel}
			onClickMoreMenuTrigger={handleClickMoreMenuItem}
			onClickCustomizeSidebarMenuItem={handleClickCustomizeItem}
			openShortcutsInSameTab={openShortcutsInSameTab}
		/>
	);

	const globalAppShortcuts = (
		<Box ref={containerRef}>
			<GlobalAppShortcuts
				shortcutItems={shortcutsWithMenuId}
				isInViewport={isInViewport}
				openShortcutsInSameTab={openShortcutsInSameTab}
			/>
		</Box>
	);

	return (
		<Stack space="space.100">
			{postItemsElem}
			{!!shortcutsWithMenuId.length &&
				(l1DragAndDropEnabled ? (
					<GroupDropIndicatorWithDragOver
						ref={groupDropIndicatorRef}
						isActive={draggingL1Group === L1_MENU_GROUPS.SHORTCUTS}
						ariaLabel={formatMessage(messages.shortcutsSectionScreenReader, { item: dragSource })}
					>
						{globalAppShortcuts}
					</GroupDropIndicatorWithDragOver>
				) : (
					<>{globalAppShortcuts}</>
				))}
			{peekItem && (
				<MenuList testId={PEEK_TEST_ID}>
					<NavItem key={peekMenuId} id={peekMenuId} item={peekItem} isInMoreMenu peek />
				</MenuList>
			)}
			{!disableMenuCustomization &&
				(l1DragAndDropEnabled ? (
					<GroupDropIndicatorWithDragOver
						ref={groupDropIndicatorRef}
						isActive={canMenuItemBeHidden}
						ariaLabel={
							isDragSourceHidden
								? formatMessage(messages.showInSidebarSectionScreenReader, { item: dragSource })
								: formatMessage(messages.hideFromSidebarSectionScreenReader, { item: dragSource })
						}
					>
						<Box xcss={cx(canMenuItemBeHidden ? styles.hidden : styles.visible)}>{moreMenu}</Box>
						<Box xcss={cx(canMenuItemBeHidden ? styles.visible : styles.hidden)}>
							{isDragSourceHidden ? <ShowInSidebarButton /> : <HideFromSidebarButton />}
						</Box>
					</GroupDropIndicatorWithDragOver>
				) : (
					moreMenu
				))}
			{postMainElem}
		</Stack>
	);
}

export interface PostMainItemsProps
	extends Pick<
		GlobalSideNavigationProps,
		'postItemsElem' | 'postMainElem' | 'openShortcutsInSameTab'
	> {
	draggingL1Group?: string | null;
}

const styles = cssMap({
	visible: {
		display: 'block',
	},
	hidden: {
		display: 'none',
	},
});

function GlobalAppShortcuts({
	isInViewport,
	openShortcutsInSameTab,
	shortcutItems,
}: {
	isInViewport?: boolean;
	openShortcutsInSameTab?: boolean;
	shortcutItems: GlobalShortcutsItem[];
}) {
	const { app, cloudId, userId, orgId, l1DragAndDropEnabled } = useOptions();

	const { fireViewedShortcutsEvent } = useNavAnalytics();

	useLayoutEffect(() => {
		if (isInViewport) {
			fireViewedShortcutsEvent(shortcutItems);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isInViewport]);

	return (
		<GlobalShortcuts
			promoteSite
			orgId={orgId}
			cloudId={cloudId}
			userId={userId}
			product={app}
			forceStatic={true} // We don't need to call the BFF in GAS because we already did (see call to useGlobalShortcutsData() above).
			testId={SHORTCUTS_TEST_ID}
			shortcutItems={shortcutItems}
			enableDragAndDrop={l1DragAndDropEnabled}
			openInSameTab={openShortcutsInSameTab}
		/>
	);
}
